import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';

import { useRoute } from 'navigation/hooks';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useServiceModeContext } from 'state/service-mode';
import { isNotNull } from 'utils/is-not-null';
import { routes } from 'utils/routing';
import { ServiceModeCategory } from 'utils/service-mode';

interface IToggleOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface IUseServiceModeCategoryResult {
  serviceModeCategory: ServiceModeCategory;
  setServiceModeCategory: Dispatch<SetStateAction<ServiceModeCategory>>;
  serviceModeCategoryOptions: IToggleOption[];
}

export interface IUseServiceModeCategory {
  (): IUseServiceModeCategoryResult;
}

export const useServiceModeCategory: IUseServiceModeCategory = () => {
  const isDeliveryEnabled = useFlag(LaunchDarklyFlag.ENABLE_DELIVERY);
  const isOnlyDeliveryEnabled = useFlag(LaunchDarklyFlag.ENABLE_DELIVERY_ONLY);
  const { pathname } = useRoute();
  const { serviceMode } = useServiceModeContext();

  const { formatMessage } = useIntl();

  const serviceModeCategoryOptions = useMemo(() => {
    if (isOnlyDeliveryEnabled) {
      return [{ label: formatMessage({ id: 'delivery' }), value: ServiceModeCategory.DELIVERY }];
    }

    return [
      { label: formatMessage({ id: 'pickUp' }), value: ServiceModeCategory.PICKUP },
      isDeliveryEnabled
        ? { label: formatMessage({ id: 'delivery' }), value: ServiceModeCategory.DELIVERY }
        : null,
    ].filter(isNotNull);
  }, [formatMessage, isDeliveryEnabled, isOnlyDeliveryEnabled]);

  const [serviceModeCategory, setServiceModeCategory] = useState(
    serviceModeCategoryOptions[0]?.value || ServiceModeCategory.PICKUP
  );

  // Keep the service mode category tabs in sync with the related routes.
  useEffect(() => {
    // Main pickup route.
    if (pathname === routes.storeLocator) {
      setServiceModeCategory(ServiceModeCategory.PICKUP);
    }

    // Delivery route.
    // If we ever change the Delivery address implementation to be a tab inside the store locator we might want to uncomment these lines
    // if (pathname === routes.address) {
    //   setServiceModeCategory(ServiceModeCategory.DELIVERY);
    // }
  }, [pathname, serviceMode]);

  return {
    serviceModeCategory,
    setServiceModeCategory,
    serviceModeCategoryOptions,
  };
};
